import { Key, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDelete } from '@/components/ConfirmDelete';
import { toast } from '@/hooks/use-toast';
import { useDeleteApiToken } from '@/hooksApi';
import { AccessTokenDataResponse } from '@/types';

interface AccessTokenRowProps {
  token: AccessTokenDataResponse;
  onCopy: (value: string, label: string) => void;
}

export const AccessTokenRow = ({ token, onCopy }: AccessTokenRowProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutate: deleteApiToken, isLoading: isDeleting } = useDeleteApiToken();

  const handleDelete = () => {
    if (isDeleting) return;
    deleteApiToken(token.key, {
      onSuccess: () => {
        queryClient.invalidateQueries(['api-token']);
        toast({ title: t('token_deleted') });
      },
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
      <div className='p-5 space-y-3'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <div className='h-9 w-9 rounded-lg bg-sd-muted flex items-center justify-center shrink-0'>
              <Key className='h-4 w-4 text-sd-muted-foreground' />
            </div>
            <h3 className='text-sm font-semibold text-sd-foreground'>{token.name}</h3>
          </div>
          <div className='flex items-center gap-1 shrink-0'>
            <button
              onClick={() => onCopy(token.key, 'API Key')}
              title={t('copy_api_key')}
              className='p-1.5 rounded-lg text-sd-muted-foreground hover:text-sd-foreground hover:bg-sd-muted transition-colors'
            >
              <Copy className='h-4 w-4' />
            </button>
            <ConfirmDelete
              title={t('delete_token')}
              description={`"${token.name}" ${t('will_be_permanently_deleted')}`}
              onConfirm={handleDelete}
              disabled={isDeleting}
            />
          </div>
        </div>

        {/* API Key */}
        <div className='space-y-1'>
          <p className='text-[11px] font-medium text-sd-muted-foreground uppercase tracking-wider'>
            {t('api_key')}
          </p>
          <div className='flex items-center rounded-lg bg-sd-muted px-3 py-2'>
            <code className='flex-1 text-xs font-mono text-sd-foreground select-all'>
              {token.key}
            </code>
          </div>
        </div>

        {/* Secret Key */}
        <div className='space-y-1'>
          <p className='text-[11px] font-medium text-sd-muted-foreground uppercase tracking-wider'>
            {t('api_secret')}
          </p>
          <div className='flex items-center rounded-lg bg-sd-muted px-3 py-2'>
            <code className='flex-1 text-xs font-mono text-sd-foreground select-all'>
              {'••••••••••••••••••••••••••'}
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};
